import { TheNightlySection } from '@news-mono/common'
import { calcRem, metrics } from '@news-mono/component-library'
import { Features, StaticRoutes } from '@news-mono/web-common'
import { CheckedComponentInformation } from 'json-react-layouts'
import { layout } from '../../App.routing'
import {
    billboardLeaderboardMrec,
    getTopicAdComponents,
    getTopicAds,
} from '../ad-units'
import { appDownloadSegment } from './app-download-segment'
import {
    latestEditionSegment,
    previousEditionsSegment,
} from './get-editions-segment'
import { cardList } from './get-cardList-segment'
import { topicSection } from './get-topic-segment'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'

export const homepage: StaticRoutes<TheNightlySection> = {
    '': ({ getAdTargeting, renderTarget, store }) => {
        const pageMetaDescription =
            'Get in front of local, national and world news and current affairs with the best of opinion, politics, business, entertainment and sport.'

        const inApp = renderTarget === 'app'
        const willDisplayOddAdsDesktop = isFeatureEnabled(
            toFeatureState(store.getState().toggles),
            'tn-homepage-odd-topic-ads',
        )

        const raiseHomepageAdSlot = isFeatureEnabled(
            toFeatureState(store.getState().toggles),
            'tn-raise-homepage-adslot',
        )

        const adComponent = raiseHomepageAdSlot
            ? [
                  layout.component({
                      type: 'ad-unit',
                      props: {
                          adType: 'inline',
                          noticePosition: 'above-center',
                          slot: {
                              id: 'mrec-one-2',
                              size: 'leaderboardBillboard1280Above',
                          },
                          padding: [64, 0, 0, 0],
                          lazyLoadingDistance: 0,
                      },
                  }),
              ]
            : getTopicAdComponents(
                  true,
                  'mrec-one-3',
                  'one',
                  64,
                  0,
                  'above-center',
                  0,
              )

        return {
            kind: 'page',
            heading: 'The Nightly',
            pageType: 'homepage',
            hideHeading: true,
            refreshOnInactive: true,
            noMetaTitleTemplate: true,
            pageMeta: {
                title: 'The Nightly | Get in front of local, national and world news and current affairs with the best of opinion, politics, business, entertainment and sport',
                description: pageMetaDescription,
                meta: [
                    {
                        property: 'og:description',
                        content: pageMetaDescription,
                    },
                    {
                        property: 'twitter:description',
                        content: pageMetaDescription,
                    },
                ],
            },
            adTargeting: getAdTargeting('home', 'default', 'home'),
            section: 'default',

            compositions: [
                layout.composition({
                    type: 'luna',
                    props: {
                        //Create account CTA 'replaces' these dividers when not in app rendition.
                        dividerConfig: {
                            column: {
                                hasBeforeAsideAndAsideDivider: inApp,
                            },
                            split: {
                                hasBeforeAsideAndAfterAsideDivider: inApp,
                            },
                        },
                    },
                    contentAreas: {
                        beforeAside: [
                            toggleCreateAccountCallout({
                                shouldHide: inApp,
                                content: layout.component({
                                    type: 'card',
                                    props: {
                                        kind: 'lead-card',
                                        dataDefinitionArgs: {
                                            type: 'curation',
                                            name: 'lead-story',
                                            offset: 0,
                                            pageSize: 1,
                                        },
                                        adTargeting: getAdTargeting(
                                            'home',
                                            'default',
                                            'home',
                                        ),
                                        imageFetchPriority: 'high',
                                        disableImageLazyLoad: true,
                                    },
                                }),
                            }),
                        ],
                        afterAside: [
                            layout.component({
                                type: 'ad-unit',
                                props: {
                                    adType: 'inline',
                                    noticePosition: 'above-center',
                                    slot: {
                                        id: 'mrec-one-1',
                                        size: 'billboardLeaderboard1280BelowMrec768Below',
                                    },
                                    padding: [0, 0, 64, 0],
                                    lazyLoadingDistance: 0,
                                },
                                feature: 'tn-raise-homepage-adslot',
                            }),
                            latestEditionSegment,
                        ],
                        aside: [
                            layout.nestedComposition({
                                type: 'box',
                                props: {
                                    horizontalSpacing: 'outerMargin',
                                    horizontalSpacingBreakpoint: 'sm',
                                },
                                contentAreas: {
                                    main: [
                                        cardList(
                                            {
                                                type: 'curation',
                                                name: 'top-stories',
                                                offset: 0,
                                                pageSize: 5,
                                            },
                                            'The Top 5',
                                        ),
                                    ],
                                },
                            }),
                            layout.nestedComposition({
                                type: 'box',
                                props: {
                                    stickyOffset:
                                        'calc(var(--stickyHeaderHeight) + 80px)',
                                    hasBackgroundFill: true,
                                },
                                contentAreas: {
                                    main: [
                                        layout.component({
                                            type: 'ad-unit',
                                            props: {
                                                adType: 'inline',
                                                noticePosition: 'above-center',
                                                slot: {
                                                    id: 'type-lead-story',
                                                    size: 'above1280Mrec',
                                                    lazyLoadingDistance: 0,
                                                },
                                            },
                                        }),
                                    ],
                                },
                            }),
                        ],
                    },
                }),
                mainContentSection([
                    layout.component({
                        type: 'tn-newsletter-signup',
                        props: {
                            text: `Sign up to The Nightly's newsletters.`,
                            subText:
                                'Get the first look at the digital newspaper, curated daily stories and breaking headlines delivered to your inbox.',
                            buttonLabel: 'Get the Newsletter',
                            mode: 'default',
                        },
                    }),
                    ...adComponent,
                ]),
                topicSection({
                    section: 'politics',
                    adPos: 1,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                    showAsideAdvert: willDisplayOddAdsDesktop,
                }),
                getTopicAds(
                    willDisplayOddAdsDesktop,
                    'mrec-two',
                    'two',
                    0,
                    'above-center',
                    0,
                ),
                ...(inApp
                    ? []
                    : [
                          mainContentSection(
                              [appDownloadSegment],
                              'web-app-download',
                              true,
                          ),
                      ]),
                topicSection({
                    section: 'australia',
                    adPos: 2,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                }),
                getTopicAds(true, 'mrec-three', 'three', 0, 'above-center', 0),
                topicSection({
                    section: 'business',
                    adPos: 3,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                    showAsideAdvert: willDisplayOddAdsDesktop,
                }),
                getTopicAds(
                    willDisplayOddAdsDesktop,
                    'mrec-four',
                    'four',
                    0,
                    'above-center',
                    0,
                ),
                topicSection({
                    section: 'opinion',
                    adPos: 4,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                }),
                getTopicAds(true, 'mrec-five', 'five', 0, 'above-center', 0),
                topicSection({
                    topic: 'from/the-new-york-times',
                    curation: 'new-york-times',
                    section: 'from',
                    adPos: 5,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                    showAsideAdvert: willDisplayOddAdsDesktop,
                }),
                getTopicAds(
                    willDisplayOddAdsDesktop,
                    'mrec-six',
                    'six',
                    0,
                    'above-center',
                    0,
                ),
                topicSection({
                    section: 'culture',
                    adPos: 6,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                }),
                getTopicAds(true, 'mrec-seven', 'seven', 0, 'above-center', 0),
                topicSection({
                    section: 'society',
                    adPos: 7,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                    showAsideAdvert: willDisplayOddAdsDesktop,
                }),
                getTopicAds(
                    willDisplayOddAdsDesktop,
                    'mrec-eight',
                    'eight',
                    0,
                    'above-center',
                    0,
                ),
                topicSection({
                    section: 'lifestyle',
                    adPos: 8,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                }),
                getTopicAds(true, 'mrec-nine', 'nine', 0, 'above-center', 0),
                topicSection({
                    section: 'sport',
                    adPos: 9,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                    showAsideAdvert: willDisplayOddAdsDesktop,
                }),
                getTopicAds(
                    willDisplayOddAdsDesktop,
                    'mrec-ten',
                    'ten',
                    0,
                    'above-center',
                    0,
                ),
                topicSection({
                    section: 'world',
                    adPos: 10,
                    adTargeting: getAdTargeting('home', 'default', 'home'),
                }),
                getTopicAds(
                    true,
                    'mrec-eleven',
                    'eleven',
                    0,
                    'above-center',
                    0,
                ),
                layout.composition({
                    type: 'mars',
                    props: {
                        sectionHeader: 'In case you missed it',
                    },
                    contentAreas: {
                        primary: [
                            layout.component({
                                type: 'card',
                                props: {
                                    kind: 'list-card',
                                    dataDefinitionArgs: {
                                        type: 'curation',
                                        name: 'in-case-you-missed-it',
                                        offset: 0,
                                        pageSize: 1,
                                    },
                                    canPlayVideoInline: true,
                                },
                            }),
                        ],
                        secondary: [
                            layout.component({
                                type: 'juliet',
                                props: {
                                    cardType: {
                                        type: 'landscape',
                                        format: 'landscape-common',
                                    },
                                    imageWidths: {
                                        mobile: '80px',
                                        tablet: '104px',
                                        desktop: '128px',
                                        fallbackWidth: 128,
                                        mode: 'fixed',
                                    },
                                    verticalSpacing: 'md',
                                    removeHorizontalGutters: true,
                                    removeTopListPadding: true,
                                    hideByline: false,
                                    topicLevel: 'parent',
                                    hideLastDivider: true,
                                    dataDefinitionArgs: {
                                        type: 'curation',
                                        name: 'in-case-you-missed-it',
                                        offset: 1,
                                        pageSize: 4,
                                    },
                                },
                            }),
                        ],
                    },
                }),
                ...(inApp
                    ? []
                    : [
                          layout.composition({
                              type: 'thor',
                              props: {
                                  hasBackgroundFill: false,
                                  containerWidth:
                                      metrics.thenightly.siteMetrics
                                          .mainContentWidth,
                                  horizontalGutters: false,
                                  verticalGutters: false,
                              },
                              contentAreas: {
                                  main: [previousEditionsSegment],
                              },
                          }),
                      ]),
                layout.composition({
                    type: 'thor',
                    props: {
                        hasBackgroundFill: false,
                        containerWidth:
                            metrics.thenightly.siteMetrics.mainContentWidth,
                        horizontalGutters: 'outerMargin',
                        verticalGutters: false,
                    },
                    contentAreas: {
                        main: billboardLeaderboardMrec(
                            'mrec-twelve',
                            'twelve',
                            0,
                            'above-center',
                            0,
                        ),
                    },
                }),
            ],
        }
    },
}

function mainContentSection(
    components: Array<CheckedComponentInformation>,
    feature?: Features,
    hasBackgroundFill?: boolean,
) {
    return layout.composition({
        type: 'thor',
        feature: feature,
        props: {
            hasBackgroundFill: false,
            containerWidth: '100%',
            horizontalGutters: false,
            verticalGutters: false,
        },
        contentAreas: {
            main: [
                layout.nestedComposition({
                    type: 'thor',
                    props: {
                        hasBackgroundFill: hasBackgroundFill,
                        containerWidth: '100%',
                        verticalGutters: false,
                        horizontalGutters: false,
                    },
                    contentAreas: {
                        main: components,
                    },
                }),
            ],
        },
    })
}

const toggleCreateAccountCallout = ({
    shouldHide,
    content,
}: {
    shouldHide: boolean
    content: CheckedComponentInformation
}) => {
    if (shouldHide) return content

    return layout.nestedComposition({
        type: 'box',
        props: {
            flex: {
                default: {
                    flexDirection: 'column',
                    gap: calcRem(40),
                },
                sm: {
                    gap: calcRem(64),
                },
                lg: {
                    gap: calcRem(80),
                },
            },
        },
        contentAreas: {
            main: [
                content,
                layout.component({
                    type: 'thenightly-create-account-cta',
                    props: {},
                }),
            ],
        },
    })
}
